import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { ncbService } from '../services/ncbService';

const { FiPhone, FiMail, FiMapPin, FiSend, FiCheckCircle, FiX, FiAlertCircle, FiInfo } = FiIcons;

const Contact = () => {
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, partial-success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [details, setDetails] = useState('');
  const [settings, setSettings] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await ncbService.getSiteSettings();
        const settingsMap = data.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {});
        setSettings(settingsMap);
      } catch (err) {
        console.warn("Settings load failed");
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formStatus === 'sending') return; // Duplicate submission guard
    
    setFormStatus('sending');
    setErrorMessage('');
    setDetails('');

    const visitorEmail = formData.email.trim();
    const adminEmail = "rvenuespaces@gmail.com";

    try {
      // 1. Save to Database - This is the primary marker of success
      console.log("[Contact] Initiating NCB Save...");
      try {
        const dbResult = await ncbService.createContactMessage(formData);
        console.log("[Contact] NCB Save Success:", dbResult);
      } catch (dbErr) {
        console.error("[Contact] NCB Save Failed:", dbErr.message);
        // If DB save fails, we consider the whole operation failed
        throw new Error('Message could not be submitted. Please try again later.');
      }

      // Clear form data immediately after successful DB save
      const submissionCopy = { ...formData };
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });

      // 2. Prepare Email Notification
      const emailHtml = `
<!DOCTYPE html>
<html>
<head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /></head>
<body style="margin:0; padding:0; background-color:#F4F4EC; font-family:Arial, sans-serif; color:#344232;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F4F4EC; padding:20px;">
    <tr><td align="center">
      <table width="100%" style="max-width:600px; background-color:#FFFFFF; border-radius:15px; overflow:hidden; border:1px solid #E8E8E0; box-shadow:0 4px 12px rgba(0,0,0,0.05);">
        <tr><td style="background-color:#4C6048; padding:30px; text-align:center;">
          <img src="https://pub-46f2aff2774547b5b8e650f23b468b16.r2.dev/branding/Rvenue%20Apartelle%20Logo%20Cropped.png" alt="Logo" style="max-width:180px;" />
          <h1 style="color:#FFFFFF; font-size:20px; margin:15px 0 0; text-transform:uppercase; letter-spacing:1px;">Inquiry Received</h1>
        </td></tr>
        <tr><td style="padding:30px; line-height:1.6;">
          <p style="font-size:16px;">Hello ${submissionCopy.name},</p>
          <p style="font-size:15px;">Thank you for contacting R.Venue Apartelle. We have received your inquiry and our team will get back to you as soon as possible.</p>
          
          <div style="background:#F9F9F5; padding:25px; border-radius:10px; margin:25px 0; border:1px solid #E8E8E0;">
            <p style="margin:0 0 10px; font-size:14px; color:#4C6048; font-weight:bold; text-transform:uppercase; letter-spacing:1px;">Inquiry Summary</p>
            <p style="margin:0 0 10px;"><strong>Name:</strong> ${submissionCopy.name}</p>
            <p style="margin:0 0 10px;"><strong>Email:</strong> ${submissionCopy.email}</p>
            ${submissionCopy.phone ? `<p style="margin:0 0 10px;"><strong>Phone:</strong> ${submissionCopy.phone}</p>` : ''}
            <p style="margin:0 0 10px;"><strong>Subject:</strong> ${submissionCopy.subject || 'General Inquiry'}</p>
            <p style="margin:20px 0 0; border-top:1px solid #E8E8E0; padding-top:15px;"><strong>Message:</strong><br/>${submissionCopy.message}</p>
          </div>
          
          <p style="font-size:14px; color:#666; margin-top:30px;">Warm regards,<br/><strong style="color:#4C6048;">R.Venue Spaces</strong></p>
        </td></tr>
        <tr><td style="background-color:#F9F9F5; padding:20px; text-align:center; font-size:12px; color:#999; border-top:1px solid #E8E8E0;">
          R.Venue Apartelle • Lucban, Quezon
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

      // 3. Send Consolided Email Notification
      console.log("[Contact] Sending consolidated email notification...");
      const emailPayload = {
        to: [adminEmail, visitorEmail],
        subject: `Inquiry Received – R.Venue Apartelle`,
        html: emailHtml,
        reply_to: visitorEmail
      };
      
      let emailSuccess = false;
      try {
        const emailResp = await fetch('/api/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailPayload)
        });

        const emailResult = await emailResp.json().catch(() => ({}));
        if (emailResp.ok) {
          emailSuccess = true;
          console.log("[Contact] Email notification sent successfully.");
        } else {
          console.warn("[Contact] Emailit rejected notification:", emailResult.error || 'Unknown Error');
        }
      } catch (emailErr) {
        console.warn("[Contact] Email transmission failed:", emailErr.message);
      }

      // 4. Final Status Determination
      if (emailSuccess) {
        setFormStatus('success');
      } else {
        // We still show a success-like state because the message IS in the database
        setFormStatus('partial-success');
      }

    } catch (err) {
      console.error("[Contact] Critical Submission Error:", err.message);
      setErrorMessage(err.message);
      setFormStatus('error');
    }
  };

  return (
    <div className="w-full bg-brand-cream min-h-screen">
      <section className="pt-40 pb-20 bg-brand-olive text-white text-center">
        <div className="container mx-auto px-6">
          <p className="font-script text-3xl text-brand-cream mb-2">Get in Touch</p>
          <h1 className="font-serif text-4xl md:text-6xl uppercase tracking-widest">Contact Us</h1>
          <p className="mt-6 text-brand-cream/80 max-w-2xl mx-auto font-light leading-relaxed">
            Whether you have questions about our rooms, rates, or local attractions in Lucban, 
            our team is here to assist you. Reach out to us and we'll ensure your stay is planned to perfection.
          </p>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1 space-y-12">
              <div>
                <h3 className="font-serif text-2xl text-brand-olive uppercase tracking-wider mb-6">Our Location</h3>
                <div className="flex items-start gap-4 text-brand-olive/70">
                  <div className="mt-1">
                    <SafeIcon icon={FiMapPin} className="text-xl text-brand-olive" />
                  </div>
                  <p className="leading-relaxed">
                    3rd Floor R.Venue Bldg.,<br />
                    Quezon Ave Cor Avenida Rizal,<br />
                    Lucban, Quezon 4328, PH
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-serif text-2xl text-brand-olive uppercase tracking-wider mb-6">Reservations</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-brand-olive/70">
                    <SafeIcon icon={FiPhone} className="text-xl text-brand-olive" />
                    <div className="flex flex-col gap-1">
                      <p>+63 917 681 8251</p>
                      <p>+63 976 340 9846</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-brand-olive/70">
                    <SafeIcon icon={FiMail} className="text-xl text-brand-olive" />
                    <p>rvenuespaces@gmail.com</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-serif text-2xl text-brand-olive uppercase tracking-wider mb-6">Operating Hours</h3>
                <div className="flex items-start gap-4 text-brand-olive/70">
                  <div className="mt-1">
                    <SafeIcon icon={FiInfo} className="text-xl text-brand-olive" />
                  </div>
                  <div className="space-y-1">
                    <p>Check-in: 2:00 PM</p>
                    <p>Check-out: 11:00 AM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 md:p-12 shadow-2xl border border-brand-olive/5">
                <h3 className="font-serif text-2xl text-brand-olive uppercase tracking-wider mb-8">Send a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Full Name</label>
                      <input type="text" required className="w-full p-4 border-b border-brand-olive/20 outline-none focus:border-brand-olive bg-brand-cream/5" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Email Address</label>
                      <input type="email" required className="w-full p-4 border-b border-brand-olive/20 outline-none focus:border-brand-olive bg-brand-cream/5" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Phone (Optional)</label>
                      <input type="tel" className="w-full p-4 border-b border-brand-olive/20 outline-none focus:border-brand-olive bg-brand-cream/5" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Subject</label>
                      <input type="text" placeholder="General Inquiry" className="w-full p-4 border-b border-brand-olive/20 outline-none focus:border-brand-olive bg-brand-cream/5" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Message</label>
                    <textarea rows="5" required className="w-full p-4 border-b border-brand-olive/20 outline-none focus:border-brand-olive resize-none bg-brand-cream/5" value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} />
                  </div>
                  
                  <button disabled={formStatus === 'sending'} className="btn-primary w-full py-5 text-sm uppercase tracking-widest font-black">
                    {formStatus === 'sending' ? 'Sending Message...' : 'Send Message'}
                  </button>
                  
                  {formStatus === 'error' && (
                    <div className="mt-8 p-6 bg-red-50 border border-red-100 rounded-lg text-center">
                      <SafeIcon icon={FiAlertCircle} className="text-red-500 text-3xl mx-auto mb-3" />
                      <p className="text-red-600 text-[10px] font-black uppercase tracking-widest">{errorMessage}</p>
                      {details && <p className="text-red-400 text-[9px] uppercase tracking-widest mt-2">{details}</p>}
                      <p className="text-brand-olive/40 text-[9px] mt-4 uppercase tracking-[0.2em]">Please try again or contact us directly.</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {(formStatus === 'success' || formStatus === 'partial-success') && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-olive/40 backdrop-blur-sm p-6">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white p-10 max-w-md w-full text-center shadow-2xl">
              <div className="w-20 h-20 bg-brand-cream rounded-full flex items-center justify-center mx-auto mb-8">
                <SafeIcon 
                  icon={formStatus === 'success' ? FiCheckCircle : FiInfo} 
                  className={`text-4xl ${formStatus === 'success' ? 'text-brand-olive' : 'text-amber-600'}`} 
                />
              </div>
              
              <h2 className="font-serif text-2xl uppercase tracking-widest text-brand-olive mb-4">
                {formStatus === 'success' ? 'Message Sent Successfully' : 'Message Received'}
              </h2>
              
              <p className="text-brand-olive/70 text-sm mb-10 leading-relaxed">
                {formStatus === 'success' 
                  ? 'Thank you for contacting R.Venue Apartelle. We’ve received your inquiry and our team will get back to you as soon as possible.'
                  : 'Your inquiry has been received and saved in our system. However, we were unable to deliver an email copy at this time. Our team will still process your request as usual.'
                }
              </p>
              
              <button onClick={() => setFormStatus('idle')} className="btn-primary w-full py-4 text-[10px] uppercase tracking-widest font-black">Close</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Contact;